import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./customFullPage.scss";

const getDirection = (from, to) => {
  if (to === from) return "none";
  return to > from ? "down" : "up";
};

const isEditable = (target) => {
  const tag = target?.tagName?.toLowerCase();
  return (
    target?.isContentEditable ||
    ["input", "textarea", "select", "button"].includes(tag)
  );
};

const CustomFullPage = ({ children, onLeave }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sections, setSections] = useState([]);
  const [isLocked, setIsLocked] = useState(false);

  // Refs to prevent re-renders loops 
  const activeIndexRef = useRef(0);
  const onLeaveRef = useRef(onLeave);

  useEffect(() => {
    onLeaveRef.current = onLeave;
    activeIndexRef.current = activeIndex;
  }, [onLeave, activeIndex]);

  // 1. Search and update list of sections
  useEffect(() => {
    if (containerRef.current) {
      const els = Array.from(containerRef.current.querySelectorAll(".section"));
      setSections(els);
    }
  }, [children]);

  // 2. Global lock (MutationObserver)
  useEffect(() => {
    const checkLock = () => {
      const isExternalLocked =
        window.getComputedStyle(document.documentElement).overflow ===
          "hidden" ||
        window.getComputedStyle(document.body).overflow === "hidden";
      setIsLocked(isExternalLocked);
    };
    const observer = new MutationObserver(checkLock);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    window.addEventListener("resize", checkLock);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkLock);
    };
  }, []);

  // 3. Smart Intersection Observer
  useEffect(() => {
    if (sections.length === 0) return;

    const observerOptions = {
      root: containerRef.current,
      // "Line" in the center of the screen for detection
      rootMargin: "-48% 0px -48% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const newIndex = sections.indexOf(entry.target);
          const oldIndex = activeIndexRef.current;

          if (newIndex !== -1 && newIndex !== oldIndex) {
            const direction = getDirection(oldIndex, newIndex);
            onLeaveRef.current?.(
              { index: oldIndex },
              { index: newIndex },
              direction
            );
            setActiveIndex(newIndex);
          }
        }
      });
    }, observerOptions);

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [sections]);

  // 4. Scroll method
  const scrollToSection = useCallback(
    (index) => {
      if (isLocked || !sections[index]) return;
      sections[index].scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [isLocked, sections]
  );

  // 5. Key handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isLocked || isEditable(e.target)) return;
      const actions = {
        ArrowDown: () => scrollToSection(activeIndex + 1),
        PageDown: () => scrollToSection(activeIndex + 1),
        " ": () => scrollToSection(activeIndex + 1),
        ArrowUp: () => scrollToSection(activeIndex - 1),
        PageUp: () => scrollToSection(activeIndex - 1),
        Home: () => scrollToSection(0),
        End: () => scrollToSection(sections.length - 1),
      };
      if (actions[e.key]) {
        e.preventDefault();
        actions[e.key]();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, isLocked, scrollToSection, sections.length]);

  return (
    <div
      ref={containerRef}
      className={`custom-fullpage ${isLocked ? "custom-fullpage--locked" : ""}`}
    >
      <main>{children}</main>

      {sections.length > 1 && (
        <nav id="fp-nav" className="right">
          <ul>
            {sections.map((_, index) => (
              <li key={index}>
                <button
                  type="button"
                  aria-label={`Section ${index + 1}`}
                  className={activeIndex === index ? "active" : ""}
                  onClick={() => scrollToSection(index)}
                >
                  <span />
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

CustomFullPage.propTypes = {
  children: PropTypes.node.isRequired,
  onLeave: PropTypes.func,
};

export default CustomFullPage;
